/**
 * "My inventions" — the Synergy view (the figure's third tab).
 *
 * Where the Overlap view answers "are two of these the same work?", this one
 * answers the opposite question: "where does one of my inventions supply
 * something another one needs?" Both read the same own-scope synergy run —
 * GET /whitespace/portfolio_synergy?scope=own — which returns, per scored pair,
 * the real cells: a FEATURE from one invention meeting a DOMAIN in the other.
 *
 * Every title, score and confidence below is a served field. Nothing is
 * averaged, rounded up or filled in; a pair with no cells says so.
 */
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getPortfolioSynergy } from '../../services/api'
import { projectTagColor } from '../../lib/whitespace'
import LoadingSkeleton from '../shared/LoadingSkeleton'
import { OWN_SCOPE_ENTITLEMENT_LINE } from './ownScopeViews'
import { MAX_MEMBERS_FOR_SYNERGY } from './CapFooter'
import type { Project } from '../../types'

const OWN = 'own' as const

export interface OwnSynergyViewProps {
  /** The inventions the caller owns, for names — never a second fetch. */
  owned: Project[]
}

export default function OwnSynergyView({ owned }: OwnSynergyViewProps) {
  const synergyQuery = useQuery({
    queryKey: ['portfolio-synergy', 'own'],
    queryFn: () => getPortfolioSynergy(null, false, OWN),
    retry: false,
  })

  const names = useMemo(() => new Map(owned.map((p) => [p.project_id, p.name])), [owned])
  const data = synergyQuery.data

  const pairs = useMemo(() => {
    const cells = data?.cells ?? []
    return (data?.pair_scores ?? []).map((pair) => ({
      ...pair,
      cells: cells.filter((c) => c.pair_key === pair.pair_key),
    }))
  }, [data])

  const truncated = data?.truncated_member_project_ids ?? []
  // Story #6762: a shared-in project has no name here — count it, never print
  // its raw id.
  const namedLeftOut = truncated.flatMap((id) => (names.has(id) ? [names.get(id) as string] : []))
  const unnamedLeftOut = truncated.length - namedLeftOut.length
  const leftOutList = [
    ...namedLeftOut,
    ...(unnamedLeftOut > 0
      ? [`${unnamedLeftOut} shared-in invention${unnamedLeftOut === 1 ? '' : 's'} not listed by name here`]
      : []),
  ].join(', ')

  if (synergyQuery.isLoading) return <LoadingSkeleton type="card" rows={4} />

  if (synergyQuery.isError) {
    return (
      <p role="alert" className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-[12.5px] text-amber-900">
        We could not load your synergy run. Nothing here is out of date — there is simply nothing to
        show until that call succeeds.
      </p>
    )
  }

  return (
    <div data-part="OwnSynergyView" className="space-y-3">
      <div className="flex items-start gap-2.5 rounded-lg border border-iflow-200 bg-iflow-50/70 px-3 py-2.5 text-[12px] leading-relaxed text-iflow-900">
        <span aria-hidden className="mt-0.5 font-bold text-iflow-600">ℹ</span>
        <p>
          <b>Where one invention supplies another.</b> A cell is a capability in one of your
          inventions meeting a need in another — the opposite question to overlap, on the same run.
          Only your own inventions are compared.
        </p>
      </div>

      {pairs.length > 0 ? (
        <p className="text-[12px] text-gray-600">
          {data?.cross_project_cell_count ?? 0}{' '}
          {(data?.cross_project_cell_count ?? 0) === 1 ? 'cell joins' : 'cells join'} two of your
          inventions, across {pairs.length} scored {pairs.length === 1 ? 'pair' : 'pairs'} ·{' '}
          <b className="font-semibold text-iflow-800">{OWN_SCOPE_ENTITLEMENT_LINE}</b>
        </p>
      ) : (
        <p className="text-[12px] text-gray-600">
          <b>Not compared yet.</b> No pair of your inventions has been scored, so there is no
          synergy result to report either way · <b className="font-semibold text-iflow-800">{OWN_SCOPE_ENTITLEMENT_LINE}</b>
        </p>
      )}

      {pairs.length === 0 ? (
        <p className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-[12.5px] text-gray-600">
          Synergy is worked out automatically from the documents in your inventions. An invention
          with no documents has nothing to compare, so add documents and check back.
        </p>
      ) : (
        <ul className="space-y-3">
          {pairs.map((pair) => (
            <li key={pair.pair_key} className="rounded-xl border border-gray-200 bg-white p-3.5">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  aria-hidden
                  className="h-3 w-3 rounded"
                  style={{ background: projectTagColor(pair.project_a_id, [pair.project_a_id, pair.project_b_id]) }}
                />
                <span className="text-[13px] font-bold text-gray-900">
                  {names.get(pair.project_a_id) ?? pair.project_a_id}
                </span>
                <span className="text-gray-400">+</span>
                <span
                  aria-hidden
                  className="h-3 w-3 rounded"
                  style={{ background: projectTagColor(pair.project_b_id, [pair.project_a_id, pair.project_b_id]) }}
                />
                <span className="text-[13px] font-bold text-gray-900">
                  {names.get(pair.project_b_id) ?? pair.project_b_id}
                </span>
                <span className="ml-auto text-[12px] text-gray-500">
                  {pair.overall_score === null ? 'not scored' : `Pair score: ${pair.overall_score}`}
                </span>
              </div>

              {pair.cells.length === 0 ? (
                <p className="mt-2 text-[11.5px] text-gray-500">
                  This pair was scored but the run kept no cell for it — there is nothing to read
                  beyond the number above.
                </p>
              ) : (
                <ul className="mt-2 divide-y divide-gray-100">
                  {pair.cells.map((cell) => (
                    <li key={`${cell.feature_component_id}-${cell.domain_component_id}`} className="py-2">
                      <p className="text-[12px] text-gray-900">
                        <b>{cell.feature_title}</b>
                        <span className="mx-1.5 text-gray-400">→</span>
                        {cell.domain_title}
                      </p>
                      <p className="mt-0.5 text-[11px] text-gray-500">
                        {cell.kind.replace(/_/g, ' ')}
                        {cell.confidence !== null && <> · confidence {(cell.confidence * 100).toFixed(0)}%</>}
                        {cell.is_cross_project && <> · joins two of your inventions</>}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}

      {truncated.length > 0 && (
        <p className="text-[11px] text-gray-500">
          Synergy compares up to {MAX_MEMBERS_FOR_SYNERGY} inventions at a time, so{' '}
          {truncated.length === 1 ? 'this one was' : 'these were'} not included in this result and
          nothing is said about {truncated.length === 1 ? 'it' : 'them'}:{' '}
          {leftOutList}.
        </p>
      )}
    </div>
  )
}
